import { useCallback, useEffect, useState } from 'react';
import api from './client';

/**
 * Fetches validation wizard context (permissions, globals, agency metadata).
 *
 * @param {Object} [params]
 * @param {string|number} [params.countyId]
 * @param {Object} [options]
 * @param {boolean} [options.skip=false] When true, bypasses the fetch.
 * @returns {{data: Object|null, isLoading: boolean, error: Error|null, refresh: () => Promise<Object>}}
 */
export function useValidationContext({ countyId } = {}, { skip = false } = {}) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(!skip);
  const [error, setError] = useState(null);

  const fetchContext = useCallback(async () => {
    return api.validate.getContext({ countyId });
  }, [countyId]);

  useEffect(() => {
    if (skip) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const response = await fetchContext();
        if (!cancelled) {
          setData(response);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fetchContext, skip]);

  const refresh = useCallback(async () => {
    if (skip) {
      return data;
    }

    setIsLoading(true);
    try {
      const response = await fetchContext();
      setData(response);
      setError(null);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [data, fetchContext, skip]);

  return {
    data,
    isLoading,
    error,
    refresh,
  };
}
